import { memo, useMemo } from 'react';
import { UnstyledButton, Group, Text, Stack, Progress } from '@mantine/core';
import { IconChecklist, IconLink, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { TaskProgressSummaryProps } from './TaskProgressSummary.types';
import { getRelatedTasksProgress } from '../../../../../../utils/taskHelpers';
import {
  PROGRESS_ICON_SIZE,
  PROGRESS_COLOR_ACTIVE,
  PROGRESS_COLOR_COMPLETE,
  CHEVRON_ICON_SIZE,
} from './constants';
import styles from './TaskProgressSummary.module.css';

/**
 * TaskProgressSummary Component
 *
 * Displays progress summary for task requirements and related tasks using progress bars.
 * Shows:
 * - Requirements progress with visual bar (if task has requirements)
 * - Related tasks progress with visual bar (if task has related tasks)
 *
 * Position: Between BasicInfoFields and TaskDescription in General tab
 * Layout: Stacked vertically with clickable rows
 * Interactive: Entire row is clickable (navigates to respective tab)
 *
 * @param task - Task object with requirements and related tasks data
 * @param onTabChange - Callback to switch tabs in TaskDetails panel
 */
export const TaskProgressSummary = memo(function TaskProgressSummary({
  task,
  onTabChange,
}: TaskProgressSummaryProps) {
  const { t } = useTranslation();

  // Calculate requirements progress
  const requirementsData = useMemo(() => {
    if (task.requirements.length === 0) return null;

    const completed = task.requirements.filter(
      (requirement) => requirement.completed
    ).length;
    const total = task.requirements.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  }, [task.requirements]);

  // Calculate related tasks progress using helper function
  const relatedTasksData = useMemo(() => {
    if (task.relatedTasks.length === 0) return null;

    return getRelatedTasksProgress(task);
  }, [task]);

  // Hide entire summary if both are empty
  if (!requirementsData && !relatedTasksData) {
    return null;
  }

  return (
    <Stack gap="xs">
      {/* Requirements Progress */}
      {requirementsData && (
        <UnstyledButton
          onClick={() => onTabChange('requirements')}
          className={styles.progressRow}
        >
          <Group justify="space-between" mb={4}>
            <Group gap="xs">
              <IconChecklist size={PROGRESS_ICON_SIZE} />
              <Text size="sm" fw={500}>
                {t('fields.requirements')}
              </Text>
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                {requirementsData.percentage}%
              </Text>
              <IconChevronRight
                size={CHEVRON_ICON_SIZE}
                className={styles.chevron}
              />
            </Group>
          </Group>
          <Progress
            value={requirementsData.percentage}
            size="xs"
            color={
              requirementsData.percentage === 100
                ? PROGRESS_COLOR_COMPLETE
                : PROGRESS_COLOR_ACTIVE
            }
          />
          <Text size="xs" c="dimmed" mt={4}>
            {requirementsData.completed}/{requirementsData.total}{' '}
            {t('common.completed').toLowerCase()}
          </Text>
        </UnstyledButton>
      )}

      {/* Related Tasks Progress */}
      {relatedTasksData && (
        <UnstyledButton
          onClick={() => onTabChange('relatedTasks')}
          className={styles.progressRow}
        >
          <Group justify="space-between" mb={4}>
            <Group gap="xs">
              <IconLink size={PROGRESS_ICON_SIZE} />
              <Text size="sm" fw={500}>
                {t('fields.relatedTasks')}
              </Text>
            </Group>
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                {relatedTasksData.percentage}%
              </Text>
              <IconChevronRight
                size={CHEVRON_ICON_SIZE}
                className={styles.chevron}
              />
            </Group>
          </Group>
          <Progress
            value={relatedTasksData.percentage}
            size="xs"
            color={
              relatedTasksData.percentage === 100
                ? PROGRESS_COLOR_COMPLETE
                : PROGRESS_COLOR_ACTIVE
            }
          />
          <Text size="xs" c="dimmed" mt={4}>
            {relatedTasksData.completed}/{relatedTasksData.total}{' '}
            {t('common.completed').toLowerCase()}
          </Text>
        </UnstyledButton>
      )}
    </Stack>
  );
});
